/* eslint-disable no-magic-numbers */
import Polygon from 'ol/geom/Polygon';
import { Coordinate } from 'ol/coordinate';
import getPolygonCoords from '@/components/Map/MapViewer/utils/mapElementsRendering/coords/getPolygonCoords';
import getEllipseCoords from '@/components/Map/MapViewer/utils/mapElementsRendering/coords/getEllipseCoords';
import { Shape, ShapeRelAbs, ShapeRelAbsBezierPoint } from '@/types/models';
import { UsePointToProjectionResult } from '@/utils/map/usePointToProjection';
import getCenter from '@/components/Map/MapViewer/utils/mapElementsRendering/coords/getCenter';

function reverseXY(
  points: Array<ShapeRelAbs | ShapeRelAbsBezierPoint>,
): Array<ShapeRelAbs | ShapeRelAbsBezierPoint> {
  const result: Array<ShapeRelAbs | ShapeRelAbsBezierPoint> = [];
  for (let i = 0; i < points.length; i += 1) {
    const point = points[i];
    if (point.type === 'REL_ABS_POINT') {
      result.push({
        type: point.type,
        absoluteX: point.absoluteY,
        absoluteY: point.absoluteX,
        relativeX: point.relativeY,
        relativeY: point.relativeX,
        relativeHeightForX: point.relativeWidthForY,
        relativeWidthForY: point.relativeHeightForX,
      });
    } else if (point.type === 'REL_ABS_BEZIER_POINT') {
      result.push({
        type: point.type,
        absoluteX1: point.absoluteY1,
        absoluteY1: point.absoluteX1,
        relativeX1: point.relativeY1,
        relativeY1: point.relativeX1,
        relativeHeightForX1: point.relativeWidthForY1,
        relativeWidthForY1: point.relativeHeightForX1,
        absoluteX2: point.absoluteY2,
        absoluteY2: point.absoluteX2,
        relativeX2: point.relativeY2,
        relativeY2: point.relativeX2,
        relativeHeightForX2: point.relativeWidthForY2,
        relativeWidthForY2: point.relativeHeightForX2,
        absoluteX3: point.absoluteY3,
        absoluteY3: point.absoluteX3,
        relativeX3: point.relativeY3,
        relativeY3: point.relativeX3,
        relativeHeightForX3: point.relativeWidthForY3,
        relativeWidthForY3: point.relativeHeightForX3,
      });
    } else {
      throw new Error('Invalid point type');
    }
  }

  return result;
}

export default function getShapePolygon({
  shape,
  x,
  y,
  width,
  height,
  mirrorX,
  mirrorY,
  pointToProjection,
  switchXY,
}: {
  shape: Shape;
  x: number;
  y: number;
  width: number;
  height: number;
  mirrorX?: boolean;
  mirrorY?: boolean;
  pointToProjection: UsePointToProjectionResult;
  switchXY?: boolean;
}): Polygon {
  let coords: Array<Coordinate> = [];
  if (shape.type === 'POLYGON') {
    let points: Array<ShapeRelAbs | ShapeRelAbsBezierPoint> = [];
    if (switchXY) {
      points = reverseXY(shape.points);
    } else {
      points = shape.points;
    }
    coords = getPolygonCoords({ points, x, y, height, width, pointToProjection });
  } else if (shape.type === 'ELLIPSE') {
    coords = getEllipseCoords({
      x,
      y,
      center: shape.center,
      radius: shape.radius,
      height,
      width,
      pointToProjection,
    });
  }

  if (mirrorX) {
    const centroid = getCenter(coords);

    coords = coords.map(coord => {
      const mirroredX = 2 * centroid[0] - coord[0];

      return [mirroredX, coord[1]];
    });
  }
  if (mirrorY) {
    const centroid = getCenter(coords);

    coords = coords.map(coord => {
      const mirroredY = 2 * centroid[1] - coord[1];

      return [coord[0], mirroredY];
    });
  }
  coords.push(coords[0]);

  return new Polygon([coords]);
}
